import { useState } from "react";
import { api, ApiError } from "../lib/api.ts";
import { useGet, useSession } from "../lib/session.tsx";
import type { BillingInfo, Invoice } from "../lib/types.ts";

const BRL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const DAY = new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo" });
const MONTH = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric", timeZone: "America/Sao_Paulo" });

const money = (cents: number) => BRL.format(cents / 100);
const day = (iso: string) => DAY.format(new Date(iso));

const STATE: Record<BillingInfo["state"], { label: string; chip: string }> = {
  trial: { label: "Período grátis", chip: "blue" },
  active: { label: "Em dia", chip: "green" },
  overdue: { label: "Fatura vencida", chip: "amber" },
  read_only: { label: "Só leitura", chip: "red" },
};

/** The store's plan and invoices, paid by Pix. Only owners get here. */
export function Billing() {
  const s = useSession();
  const data = useGet<{ billing: BillingInfo }>("/v1/billing");
  const [open, setOpen] = useState<Invoice | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const b = data.data?.billing;
  // Hide the Pix once the invoice shows as paid.
  const shown = open && b?.invoices?.find((i) => i.id === open.id)?.status === "open" ? open : null;

  async function pay(inv: Invoice) {
    setBusy(inv.id);
    setErr(null);
    try {
      const r = await api<{ invoice: Invoice }>("POST", `/v1/billing/invoices/${inv.id}/pix`);
      setOpen(r.invoice);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Erro");
    } finally {
      setBusy(null);
    }
  }

  async function refresh() {
    data.reload();
    await s.reloadTenants();
  }

  return (
    <div className="stack" style={{ maxWidth: 820 }}>
      <div className="topbar">
        <h1>Cobrança</h1>
      </div>
      {data.error && <p className="error">{data.error.message}</p>}
      {b && (
        <div className="card stack">
          <div className="row" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <strong>Plano mensal</strong>
            {b.priceCents !== undefined && <span>{money(b.priceCents)} por mês</span>}
            <span className={`chip ${STATE[b.state].chip}`}>{STATE[b.state].label}</span>
          </div>
          <p className="muted" style={{ margin: 0 }}>
            {b.state === "trial" && b.trialEndsAt && <>Grátis até {day(b.trialEndsAt)}. A primeira fatura sai nesse dia. </>}
            {b.state === "overdue" && b.readOnlyAt && <>Pague até {day(b.readOnlyAt)} para a loja não ficar só leitura. </>}
            {b.state === "read_only" && <>A loja só lê dados até a fatura ser paga. Nada foi apagado. </>}
            {b.nextInvoiceAt && b.state !== "trial" && <>Próxima fatura em {day(b.nextInvoiceAt)}. </>}
            Pagamento por Pix; a fatura vence 5 dias depois de emitida.
          </p>
        </div>
      )}
      {err && <p className="error">{err}</p>}
      {shown?.pix && (
        <div className="card stack">
          <strong>Pix de {money(shown.amountCents)}</strong>
          {shown.pix.qrCodeImage && <img className="pix-qr" src={shown.pix.qrCodeImage} alt="QR code do Pix" />}
          <label className="fld">
            Pix copia e cola
            <textarea className="pix-code" readOnly rows={3} value={shown.pix.brCode} onFocus={(e) => e.currentTarget.select()} />
          </label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              className="btn primary"
              onClick={async () => {
                await navigator.clipboard.writeText(shown.pix!.brCode);
                s.toast("Código copiado.");
              }}
            >
              Copiar código
            </button>
            {shown.pix.paymentLink && (
              <a className="btn" href={shown.pix.paymentLink} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                Abrir página de pagamento
              </a>
            )}
            <button className="btn" onClick={refresh}>
              Já paguei
            </button>
          </div>
          <p className="muted" style={{ margin: 0 }}>
            Válido até {day(shown.pix.expiresAt)}. A confirmação chega em alguns segundos depois do pagamento.
          </p>
        </div>
      )}
      <div className="card list">
        <strong>Faturas</strong>
        {b?.invoices?.length === 0 && <span className="muted">Nenhuma fatura ainda.</span>}
        {b?.invoices?.map((inv) => (
          <div key={inv.id} className="list-item" style={{ flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{MONTH.format(new Date(inv.periodStart))}</span>
            <span>{money(inv.amountCents)}</span>
            {inv.status === "paid" && <span className="chip green">Paga em {day(inv.paidAt!)}</span>}
            {inv.status === "open" && (
              <span className={`chip ${new Date(inv.dueAt) < new Date() ? "amber" : "gray"}`}>Vence em {day(inv.dueAt)}</span>
            )}
            {inv.status === "canceled" && <span className="chip gray">Cancelada</span>}
            <span className="spacer" />
            {inv.status === "open" && (
              <button className="btn primary" disabled={busy === inv.id} onClick={() => pay(inv)}>
                {busy === inv.id ? "Gerando…" : "Pagar com Pix"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
